"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.VALUE_SLICE_KEYS = exports.NC_PINS_SLICE_KEY = exports.SIGNAL_MAPPING_SLICE_KEY = exports.STRUCTURE_SLICE_KEY = void 0;
exports.buildFormStructureInstances = buildFormStructureInstances;
exports.buildFormSignalMappingRows = buildFormSignalMappingRows;
exports.buildFormStateFromSlices = buildFormStateFromSlices;
exports.createSigmaModelFormSlices = createSigmaModelFormSlices;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const array_submodels_1 = require("../../PackageStore/ComponentArrays/array-submodels");
const array_resolution_adapter_1 = require("../../Wizard/Definitions/steps/forms/sigma-model-entry-form/array-resolution-adapter");
const form_latest_1 = require("../../Wizard/Definitions/steps/forms/sigma-model-entry-form/form-schemas/form-latest");
const no_connect_signal_1 = require("../../Wizard/Definitions/steps/forms/sigma-model-entry-form/no-connect-signal");
const form_model_shape_1 = require("../../Wizard/Definitions/steps/forms/sigma-model-entry-form/form-model-shape");
const comparison_spec_1 = require("../comparison-spec");
const boundary_conditions_comparison_1 = require("../comparisons/boundary-conditions.comparison");
const form_signal_mapping_comparison_1 = require("../comparisons/form-signal-mapping.comparison");
const nc_pins_comparison_1 = require("../comparisons/nc-pins.comparison");
const form_structure_comparison_1 = require("../comparisons/form-structure.comparison");
const scalar_comparison_1 = require("../comparisons/scalar.comparison");
const sigma_model_parameters_comparison_1 = require("../comparisons/sigma-model-parameters.comparison");
const consensus_assembler_1 = require("../consensus-assembler/consensus-assembler");
const general_package_slices_1 = require("../general-slices/general-package-slices");
const multi_reviewer_consensus_service_1 = require("../multi-reviewer-consensus.service");
const slice_key_1 = require("../slice-key");
const slice_scoped_logger_1 = require("../slice-scoped-logger");
const slice_contracts_1 = require("../slice-contracts");
exports.STRUCTURE_SLICE_KEY = slice_key_1.SliceKey.Structure;
exports.SIGNAL_MAPPING_SLICE_KEY = slice_key_1.SliceKey.SignalMapping;
exports.NC_PINS_SLICE_KEY = slice_key_1.SliceKey.NcPins;
const formSliceLogger = (0, sigma_engine_shared_1.generateCompatibleConsoleLogger)();
class SigmaModelFormSlice {
    resolveSpec(_payloads) {
        return this.spec;
    }
    run(submissions, policies, logger) {
        const service = new multi_reviewer_consensus_service_1.MultiReviewerConsensusService(new slice_scoped_logger_1.SliceScopedLogger(this.sliceKey, logger ?? formSliceLogger));
        const projected = submissions.map(([reviewerId, payload]) => [reviewerId, this.buildData(payload)]);
        const spec = this.resolveSpec(submissions.map(([, payload]) => payload));
        return service.run(projected, {
            spec,
            confidencePolicy: policies.confidencePolicy,
            triagePolicy: policies.triagePolicy,
            assembler: new consensus_assembler_1.ConsensusAssembler(),
        });
    }
    autoResolveColumnNames() {
        return [...this.spec.autoResolveColumns.keys()].map(String);
    }
    keyColumnNames() {
        const alignment = this.spec.alignment;
        return alignment instanceof comparison_spec_1.CellAlignment ? alignment.keyColumns.map(String) : [];
    }
}
function anyArrayPayload(payloads) {
    return payloads.some((payload) => (0, slice_contracts_1.firstSigmaSubModel)(payload) !== undefined);
}
function parseSubModelRef(subModelId) {
    const ref = (0, array_submodels_1.parseSubModelId)(subModelId);
    if (ref === null) {
        return { prefix: subModelId, index: 0 };
    }
    return { prefix: ref.prefix, index: ref.ordinal - 1 };
}
function repeatableBlockByModelSignal(payload, getFormDefinition) {
    const definition = getFormDefinition(payload.sigmaModelType);
    return new Map((definition?.signals ?? []).map((signal) => [String(signal.id), signal.repeatableBlock]));
}
function blockRefOfSignal(signal, blockByModelSignal) {
    const name = signal.blockInfo?.name ??
        (signal.modelSignal === undefined || signal.modelSignal === null ?
            null
            : (blockByModelSignal.get(String(signal.modelSignal)) ?? null));
    return (0, form_latest_1.blockRefFor)(name, signal.blockInfo?.index ?? 0);
}
function buildFormStructureInstances(payload, getFormDefinition) {
    const blockByModelSignal = repeatableBlockByModelSignal(payload, getFormDefinition);
    const instances = [];
    const seen = new Set();
    const push = (instance) => {
        const key = `${instance.kind}:${instance.blockName}:${instance.index}`;
        if (!seen.has(key)) {
            seen.add(key);
            instances.push(instance);
        }
    };
    const signals = (0, slice_contracts_1.firstSigmaSubModel)(payload) === undefined ? payload.signals : [];
    for (const signal of signals) {
        const ref = blockRefOfSignal(signal, blockByModelSignal);
        if (ref === null) {
            continue;
        }
        push({
            kind: form_structure_comparison_1.FormStructureInstanceKind.BlockInstance,
            blockName: ref.name,
            index: ref.index,
        });
    }
    for (const submodel of payload.spiceModels.filter(sigma_engine_shared_1.SPICE.isSubModelSigmaSubModel)) {
        const { prefix, index } = parseSubModelRef(submodel.subModelId);
        push({ kind: form_structure_comparison_1.FormStructureInstanceKind.ArrayElement, blockName: prefix, index });
    }
    return instances;
}
function buildFormSignalMappingRows(payload, getFormDefinition) {
    const sigmaSubModels = payload.spiceModels.filter(sigma_engine_shared_1.SPICE.isSubModelSigmaSubModel);
    if (sigmaSubModels.length === 0) {
        const blockByModelSignal = repeatableBlockByModelSignal(payload, getFormDefinition);
        const modelSignalById = modelSignalsById(getFormDefinition(payload.sigmaModelType)?.signals);
        return payload.signals
            .filter((signal) => !(0, no_connect_signal_1.isNoConnectSignal)(signal))
            .map((signal) => {
            const ref = blockRefOfSignal(signal, blockByModelSignal);
            const modelSignalRef = String(signal.modelSignal ?? signal.id);
            return {
                modelSignalRef,
                blockName: ref?.name ?? '',
                blockIndex: ref?.index ?? -1,
                signalName: signal.name,
                pins: [...(payload.pinout[String(signal.id)] ?? [])],
                ...operatorSetFloatingBehavior(modelSignalById.get(modelSignalRef), signal.floatingBehavior),
            };
        });
    }
    const signalById = new Map(payload.signals.map((signal) => [String(signal.id), signal]));
    const rows = [];
    const connectedSignalIds = new Set();
    for (const submodel of sigmaSubModels) {
        const { prefix, index } = parseSubModelRef(submodel.subModelId);
        const terminalById = modelSignalsById(getFormDefinition(submodel.modelId)?.signals);
        for (const connection of submodel.modelInfo.signalConnectionMap) {
            const componentSignalId = String(connection.componentSignalId);
            const modelSignalRef = String(connection.modelSignalId);
            const signal = signalById.get(componentSignalId);
            connectedSignalIds.add(componentSignalId);
            rows.push({
                modelSignalRef,
                blockName: prefix,
                blockIndex: index,
                signalName: signal?.name ?? '',
                pins: [...(payload.pinout[componentSignalId] ?? [])],
                ...operatorSetFloatingBehavior(terminalById.get(modelSignalRef), signal?.floatingBehavior),
            });
        }
    }
    for (const signal of payload.signals) {
        const signalId = String(signal.id);
        if ((0, no_connect_signal_1.isNoConnectSignal)(signal) || connectedSignalIds.has(signalId)) {
            continue;
        }
        rows.push({
            modelSignalRef: signalId,
            blockName: signal.blockInfo?.name ?? '',
            blockIndex: signal.blockInfo?.index ?? -1,
            signalName: signal.name,
            pins: [...(payload.pinout[signalId] ?? [])],
            ...operatorSetFloatingBehavior(undefined, signal.floatingBehavior),
        });
    }
    return rows;
}
function modelSignalsById(signals) {
    return new Map((signals ?? []).map((signal) => [String(signal.id), signal]));
}
function operatorSetFloatingBehavior(modelSignal, floatingBehavior) {
    return (0, form_model_shape_1.floatingBehaviorField)((0, form_model_shape_1.isFloatingBehaviorOperatorSet)(modelSignal) ? floatingBehavior : ((0, form_model_shape_1.recoverFloatingBehaviorChoice)(modelSignal, floatingBehavior)));
}
function buildFormStateFromSlices(args) {
    const isArray = args.elementSigmaModelType !== '';
    const definition = args.getFormDefinition(isArray ? args.elementSigmaModelType : args.parentSigmaModelType);
    const modelSignalById = new Map((definition?.signals ?? []).map((signal) => [String(signal.id), signal]));
    const pinAssignments = args.mappingRows.map((row) => {
        const modelSignal = modelSignalById.get(row.modelSignalRef);
        const isUnbound = definition !== null && modelSignal === undefined;
        const blockName = isArray || isUnbound ? row.blockName : (modelSignal?.repeatableBlock ?? null);
        return {
            modelSignalId: isUnbound ? form_latest_1.UNBOUND_MODEL_SIGNAL_ID : row.modelSignalRef,
            block: (0, form_latest_1.blockRefFor)(blockName, row.blockIndex),
            nameOverride: row.signalName === '' ? null : row.signalName,
            pins: [...row.pins],
            ...(isUnbound ? { packageSignalId: row.modelSignalRef } : {}),
            ...(row.floatingBehavior !== undefined ? { floatingBehavior: row.floatingBehavior } : {}),
        };
    });
    return {
        formVersion: form_latest_1.SIGMA_MODEL_ENTRY_FORM_LATEST_VERSION,
        parentSigmaModelType: args.parentSigmaModelType,
        elementSigmaModelType: isArray ? args.elementSigmaModelType : null,
        pinCount: null,
        arrayConfirmed: isArray ? true : null,
        pinAssignments,
        ncPins: [...args.nc.pins],
        ...(args.nc.floatingBehavior !== undefined ? { ncFloatingBehavior: args.nc.floatingBehavior } : {}),
        parameters: args.parameters,
    };
}
class SigmaModelTypeSlice extends SigmaModelFormSlice {
    constructor() {
        super(...arguments);
        this.sliceKey = slice_contracts_1.SIGMA_MODEL_TYPE_SLICE_KEY;
        this.maskPolicy = slice_contracts_1.SliceMaskPolicy.None;
        this.spec = new scalar_comparison_1.ScalarComparison(slice_contracts_1.SIGMA_MODEL_TYPE_SLICE_KEY);
    }
    appliesTo() {
        return true;
    }
    buildData(payload) {
        return payload.sigmaModelType;
    }
}
class ElementSigmaModelTypeSlice extends SigmaModelFormSlice {
    constructor() {
        super(...arguments);
        this.sliceKey = slice_contracts_1.ELEMENT_SIGMA_MODEL_TYPE_SLICE_KEY;
        this.maskPolicy = slice_contracts_1.SliceMaskPolicy.ModelType;
        this.spec = new scalar_comparison_1.ScalarComparison(slice_contracts_1.ELEMENT_SIGMA_MODEL_TYPE_SLICE_KEY);
    }
    appliesTo(payloads) {
        return anyArrayPayload(payloads);
    }
    buildData(payload) {
        return (0, slice_contracts_1.readElementSigmaModelType)(payload);
    }
}
class StructureSlice extends SigmaModelFormSlice {
    constructor(getFormDefinition) {
        super();
        this.getFormDefinition = getFormDefinition;
        this.sliceKey = exports.STRUCTURE_SLICE_KEY;
        this.maskPolicy = slice_contracts_1.SliceMaskPolicy.ElementType;
        this.spec = new form_structure_comparison_1.FormStructureComparison();
    }
    appliesTo(payloads) {
        return payloads.some((payload) => buildFormStructureInstances(payload, this.getFormDefinition).length > 0);
    }
    buildData(payload) {
        return buildFormStructureInstances(payload, this.getFormDefinition);
    }
}
class SignalMappingSlice extends SigmaModelFormSlice {
    constructor(getFormDefinition) {
        super();
        this.getFormDefinition = getFormDefinition;
        this.sliceKey = exports.SIGNAL_MAPPING_SLICE_KEY;
        this.maskPolicy = slice_contracts_1.SliceMaskPolicy.ElementType;
        this.spec = new form_signal_mapping_comparison_1.FormSignalMappingComparison();
    }
    appliesTo() {
        return true;
    }
    buildData(payload) {
        return buildFormSignalMappingRows(payload, this.getFormDefinition);
    }
}
class NcPinsSlice extends SigmaModelFormSlice {
    constructor() {
        super(...arguments);
        this.sliceKey = exports.NC_PINS_SLICE_KEY;
        this.maskPolicy = slice_contracts_1.SliceMaskPolicy.None;
        this.spec = new nc_pins_comparison_1.NcPinsComparison();
    }
    appliesTo(payloads) {
        return payloads.some((payload) => payload.signals.some(no_connect_signal_1.isNoConnectSignal));
    }
    buildData(payload) {
        const floatingBehavior = payload.signals.find(no_connect_signal_1.isNoConnectSignal)?.floatingBehavior;
        return {
            pins: (0, no_connect_signal_1.readNoConnectPinsFromPayload)(payload),
            ...(floatingBehavior !== undefined ? { floatingBehavior } : {}),
        };
    }
}
function resolveParametersSpec(resolver, modelType) {
    if (resolver === undefined || modelType === undefined || modelType.length === 0) {
        return new sigma_model_parameters_comparison_1.SigmaModelParametersComparison();
    }
    return new sigma_model_parameters_comparison_1.SigmaModelParametersComparison({}, resolver(modelType));
}
class ParametersSlice extends SigmaModelFormSlice {
    constructor(paramUnitsResolver) {
        super();
        this.paramUnitsResolver = paramUnitsResolver;
        this.sliceKey = slice_contracts_1.PARAMETERS_SLICE_KEY;
        this.maskPolicy = slice_contracts_1.SliceMaskPolicy.ModelType;
        this.spec = new sigma_model_parameters_comparison_1.SigmaModelParametersComparison();
    }
    appliesTo(payloads) {
        return payloads.some((payload) => Object.keys(payload.parameters).length > 0);
    }
    buildData(payload) {
        return payload.parameters;
    }
    resolveSpec(payloads) {
        return resolveParametersSpec(this.paramUnitsResolver, payloads[0]?.sigmaModelType);
    }
}
class ElementParametersSlice extends SigmaModelFormSlice {
    constructor(paramUnitsResolver) {
        super();
        this.paramUnitsResolver = paramUnitsResolver;
        this.sliceKey = slice_contracts_1.ELEMENT_PARAMETERS_SLICE_KEY;
        this.maskPolicy = slice_contracts_1.SliceMaskPolicy.ElementType;
        this.spec = new sigma_model_parameters_comparison_1.SigmaModelParametersComparison();
    }
    appliesTo(payloads) {
        return anyArrayPayload(payloads);
    }
    buildData(payload) {
        return (0, array_resolution_adapter_1.readArrayElementParametersFromSubModels)(payload.spiceModels);
    }
    resolveSpec(payloads) {
        const elementType = payloads[0] === undefined ? undefined : (0, slice_contracts_1.readElementSigmaModelType)(payloads[0]);
        return resolveParametersSpec(this.paramUnitsResolver, elementType);
    }
}
class SilentFormSliceLogger {
    info() { }
    warn() { }
    error() { }
}
const APPLICABILITY_PROBE_LOGGER = new SilentFormSliceLogger();
class FormBoundaryConditionsSlice extends SigmaModelFormSlice {
    constructor(sliceKey, kind) {
        super();
        this.sliceKey = sliceKey;
        this.kind = kind;
        this.spec = new boundary_conditions_comparison_1.BoundaryConditionsComparison();
        this.maskPolicy = slice_contracts_1.SliceMaskPolicy.None;
    }
    appliesTo(payloads) {
        return payloads.some((payload) => this.buildData(payload, APPLICABILITY_PROBE_LOGGER).length > 0);
    }
    buildData(payload, logger = formSliceLogger) {
        return (0, general_package_slices_1.buildBoundaryConditionRows)({ boundaries: payload.boundaries ?? [], signals: payload.signals, modes: [] }, this.kind, logger);
    }
}
function createSigmaModelFormSlices(paramUnitsResolver, definitionResolver = () => null) {
    return [
        new SigmaModelTypeSlice(),
        new ElementSigmaModelTypeSlice(),
        new StructureSlice(definitionResolver),
        new SignalMappingSlice(definitionResolver),
        new NcPinsSlice(),
        new ParametersSlice(paramUnitsResolver),
        new ElementParametersSlice(paramUnitsResolver),
        new FormBoundaryConditionsSlice(general_package_slices_1.AMR_BOUNDARIES_SLICE_KEY, general_package_slices_1.BoundaryConditionKind.Amr),
        new FormBoundaryConditionsSlice(general_package_slices_1.OC_BOUNDARIES_SLICE_KEY, general_package_slices_1.BoundaryConditionKind.Oc),
    ];
}
exports.VALUE_SLICE_KEYS = [
    exports.STRUCTURE_SLICE_KEY,
    exports.SIGNAL_MAPPING_SLICE_KEY,
    exports.NC_PINS_SLICE_KEY,
    slice_contracts_1.PARAMETERS_SLICE_KEY,
    slice_contracts_1.ELEMENT_PARAMETERS_SLICE_KEY,
    general_package_slices_1.AMR_BOUNDARIES_SLICE_KEY,
    general_package_slices_1.OC_BOUNDARIES_SLICE_KEY,
];
//# sourceMappingURL=sigma-model-form-slices.js.map